
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { auth, db, ensureAuthenticated, isTokenNearExpiry, forceTokenRefresh, backupTripToLocalStorage, getUnsavedTripBackup, clearTripBackup, UnsavedTripBackup } from '../services/firebase';
import { onAuthStateChanged, onIdTokenChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { sanitizeForFirestore } from '../services/firestoreUtils';
import { UserProfile, TripData, UserInput } from '../types';
import { useToast } from '../contexts/ToastContext';

// Admin emails that bypass the payment gate
export const ADMIN_EMAILS = ['LV@auratraveler.com', 'lucas@fiftyandfive.com'];

// Check admin status against both Firestore profile email AND Firebase Auth email.
export const isAdminEmail = (profileEmail?: string | null) => {
  if (profileEmail && ADMIN_EMAILS.includes(profileEmail.toLowerCase())) return true;
  const authEmail = auth?.currentUser?.email;
  if (authEmail && ADMIN_EMAILS.includes(authEmail.toLowerCase())) return true;
  return false;
};

export interface UseAuthStateReturn {
  user: UserProfile | null;
  setUser: React.Dispatch<React.SetStateAction<UserProfile | null>>;
  authLoading: boolean;
  showLanding: boolean;
  setShowLanding: React.Dispatch<React.SetStateAction<boolean>>;
  showSessionWarning: boolean;
  setShowSessionWarning: React.Dispatch<React.SetStateAction<boolean>>;
  authError: string | null;
  setAuthError: React.Dispatch<React.SetStateAction<string | null>>;
  authUserRef: React.MutableRefObject<UserProfile | null>;
  userRef: React.MutableRefObject<UserProfile | null>;
  isGeneratingRef: React.MutableRefObject<boolean>;
  showUnsavedTripBanner: boolean;
  setShowUnsavedTripBanner: React.Dispatch<React.SetStateAction<boolean>>;
  unsavedTripBackup: UnsavedTripBackup | null;
  setUnsavedTripBackup: React.Dispatch<React.SetStateAction<UnsavedTripBackup | null>>;
  handleLogin: (loggedInUser: UserProfile) => void;
  handleLogout: () => Promise<void>;
  handleSessionRefresh: () => Promise<void>;
  handleDismissAuthError: () => void;
  handleDismissUnsavedTrip: () => void;
  handlePaymentSuccess: (credits: number) => Promise<void>;
  showPayment: boolean;
  setShowPayment: React.Dispatch<React.SetStateAction<boolean>>;
  paymentMode: 'upgrade' | 'manage';
  setPaymentMode: React.Dispatch<React.SetStateAction<'upgrade' | 'manage'>>;
  isUserMenuOpen: boolean;
  setIsUserMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useAuthState(): UseAuthStateReturn {
  const toast = useToast();

  // Quick auth hint: if we've never logged in, skip the loading spinner entirely
  const hasAuthHint = typeof window !== 'undefined' && localStorage.getItem('aura_auth_hint') === '1';
  const [user, setUser] = useState<UserProfile | null>(null);
  const [authLoading, setAuthLoading] = useState(hasAuthHint);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentMode, setPaymentMode] = useState<'upgrade' | 'manage'>('upgrade');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showLanding, setShowLanding] = useState(false);

  // Session & Auth Token State
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [showUnsavedTripBanner, setShowUnsavedTripBanner] = useState(false);
  const [unsavedTripBackup, setUnsavedTripBackup] = useState<UnsavedTripBackup | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const isGeneratingRef = useRef(false);
  const authUserRef = useRef<UserProfile | null>(null);
  const userRef = useRef<UserProfile | null>(user);

  // Keep userRef in sync
  useEffect(() => { userRef.current = user; }, [user]);

  // --- FIREBASE AUTH LISTENER ---
  useEffect(() => {
    if (!auth || !db) {
      setAuthLoading(false);
      return;
    }

    const authTimeout = setTimeout(() => setAuthLoading(false), 400);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      clearTimeout(authTimeout);
      if (firebaseUser) {
        localStorage.setItem('aura_auth_hint', '1');

        const currentUser = authUserRef.current;
        if (currentUser && currentUser.id === firebaseUser.uid) {
          setAuthLoading(false);
          return;
        }

        const docRef = doc(db!, "users", firebaseUser.uid);
        try {
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            setUser(docSnap.data() as UserProfile);
          } else {
            console.warn("User authenticated but no profile found in Firestore. Creating one...");
            const newProfile: UserProfile = {
              id: firebaseUser.uid,
              name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Traveler',
              email: firebaseUser.email || '',
              avatarUrl: firebaseUser.photoURL || undefined,
              hasLicense: false,
              credits: 1,
              role: 'user',
              lastSearchTimestamp: null,
              createdAt: Date.now()
            };
            await setDoc(docRef, sanitizeForFirestore(newProfile));
            setUser(newProfile);
          }
        } catch (err: any) {
          const isOfflineError = err.code === 'unavailable' || (err.message?.includes("offline") && err.code !== 'failed-precondition');

          if (isOfflineError) {
            console.warn("Aura Traveler is currently offline. Using limited profile.");
            setUser({
              id: firebaseUser.uid,
              name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Traveler',
              email: firebaseUser.email || '',
              hasLicense: false,
              credits: 0,
              lastSearchTimestamp: Date.now()
            });
            if (!isGeneratingRef.current) {
              toast.warning("You're offline. Some features may be unavailable until connectivity is restored.");
            }
          } else if (err.code === 'failed-precondition') {
            console.warn("Firestore persistence issue (multi-tab?). Using Firebase Auth profile.");
            setUser({
              id: firebaseUser.uid,
              name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Traveler',
              email: firebaseUser.email || '',
              avatarUrl: firebaseUser.photoURL || undefined,
              hasLicense: false,
              credits: 1,
              lastSearchTimestamp: null,
              createdAt: Date.now()
            });
          } else {
            console.error("Firestore Auth Profile Error:", err);
            setUser({
              id: firebaseUser.uid,
              name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Traveler',
              email: firebaseUser.email || '',
              avatarUrl: firebaseUser.photoURL || undefined,
              hasLicense: false,
              credits: 1,
              lastSearchTimestamp: null,
              createdAt: Date.now()
            });
          }
        }
      } else {
        setUser(null);
        localStorage.removeItem('aura_auth_hint');
      }
      setAuthLoading(false);
    });
    return () => { unsubscribe(); clearTimeout(authTimeout); };
  }, []);

  // --- TOKEN REFRESH & SESSION TIMEOUT ---
  // Note: tripData/userData/headerImage are passed via refs from the trip hook
  // This effect uses isGeneratingRef and userRef which are kept in sync
  useEffect(() => {
    if (!auth) return;

    const unsubToken = onIdTokenChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setShowSessionWarning(false);
      }
      // Note: backup logic for trip data during auth loss is handled in useTrip
    });

    const sessionCheckInterval = setInterval(() => {
      if (auth?.currentUser && isTokenNearExpiry()) {
        setShowSessionWarning(true);
      }
    }, 60 * 1000);

    return () => {
      unsubToken();
      clearInterval(sessionCheckInterval);
    };
  }, []);

  // --- CHECK FOR UNSAVED TRIP BACKUP ON LOGIN ---
  useEffect(() => {
    if (user && auth?.currentUser) {
      const backup = getUnsavedTripBackup();
      if (backup && backup.userId === user.id) {
        setUnsavedTripBackup(backup);
        setShowUnsavedTripBanner(true);
      }
    }
  }, [user]);

  // --- BODY BACKGROUND ---
  useEffect(() => {
    document.body.style.backgroundColor = showLanding ? '#0A0A0A' : '';
  }, [showLanding]);

  // Auth Handlers
  const handleLogin = useCallback((loggedInUser: UserProfile) => {
    localStorage.setItem('aura_auth_hint', '1');
    authUserRef.current = loggedInUser;
    setUser(loggedInUser);
    setShowLanding(false);
  }, []);

  const handleLogout = useCallback(async () => {
    if (auth) {
      await signOut(auth);
    }
    document.documentElement.style.removeProperty('--color-gold');
    document.documentElement.style.removeProperty('--color-accent');
    setShowLanding(true);
    localStorage.removeItem('aura_auth_hint');
    clearTripBackup();
    setIsUserMenuOpen(false);
    if (!auth) setUser(null);
  }, []);

  const handleSessionRefresh = async () => {
    const success = await forceTokenRefresh();
    if (success) {
      setShowSessionWarning(false);
    } else {
      setAuthError("Could not refresh your session. Please sign in again.");
    }
  };

  const handleDismissAuthError = () => {
    setAuthError(null);
  };

  const handleDismissUnsavedTrip = () => {
    clearTripBackup();
    setShowUnsavedTripBanner(false);
    setUnsavedTripBackup(null);
  };

  const handlePaymentSuccess = async (credits: number) => {
    if (!user) return;
    const newCredits = user.credits + credits;
    const updatedUser = { ...user, hasLicense: true, credits: newCredits };
    setUser(updatedUser);

    if (db) {
      await updateDoc(doc(db, "users", user.id), { hasLicense: true, credits: newCredits })
        .catch(e => { console.error("Failed to sync license update", e); toast.error("The Concierge couldn\u2019t update your license. Please refresh."); });
    }
    setShowPayment(false);
  };

  return {
    user,
    setUser,
    authLoading,
    showLanding,
    setShowLanding,
    showSessionWarning,
    setShowSessionWarning,
    authError,
    setAuthError,
    authUserRef,
    userRef,
    isGeneratingRef,
    showUnsavedTripBanner,
    setShowUnsavedTripBanner,
    unsavedTripBackup,
    setUnsavedTripBackup,
    handleLogin,
    handleLogout,
    handleSessionRefresh,
    handleDismissAuthError,
    handleDismissUnsavedTrip,
    handlePaymentSuccess,
    showPayment,
    setShowPayment,
    paymentMode,
    setPaymentMode,
    isUserMenuOpen,
    setIsUserMenuOpen,
  };
}
